import {
	NavigationAction,
	useFocusEffect,
	useIsFocused,
	useNavigation,
	usePreventRemove,
} from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeModules, Platform, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../store';
import { updateProfileRequest } from '../../store/auth/authActions';
import {
	duelsCurrentRefresh,
	duelsHubRequest,
	duelsJoinErrorDismiss,
	duelsJoinRequest,
	duelsPlayResume,
	duelsPlayStop,
	duelsQuitRequest,
	duelsReconnectRequest,
	duelsResultOpened,
	duelsScreenMounted,
	duelsScreenUnmounted,
} from '../../store/duels/duelsActions';
import {
	selectCurrentDuel,
	selectDuelsConnection,
	selectDuelsHub,
	selectDuelsHubErrorKey,
	selectDuelsJoinErrorKey,
	selectDuelsJoining,
	selectDuelSnapshot,
	selectDuelsQuitErrorKey,
	selectDuelsQuitting,
	selectPendingResultDuelId,
	selectPlayingDuelId,
} from '../../store/duels/duelsReducer';
import { notificationsRequest } from '../../store/userNotifications/notificationsActions';
import { selectUnreadCount } from '../../store/userNotifications/notificationsReducer';
import { playSound } from '../../utils/sound';
import {
	DuelDivisionCard,
	DuelHero,
	DuelHistoryShortcut,
	DuelInProgressCard,
	DuelSeasonCard,
} from '../Components/Duels/DuelHubCards';
import { DuelHud, useDuelHudLayout } from '../Components/Duels/DuelHud';
import { DUEL_MODAL_ORIENTATIONS } from '../Components/Duels/duelHudTheme';
import { DuelRunCompleteOverlay } from '../Components/Duels/DuelRunCompleteOverlay';
import { duelText } from '../Components/Duels/duelsText';
import { DuelLoading, DuelNotice, DuelsPage, duelTextStyles } from '../Components/Duels/DuelsUi';
import GameTable from '../Components/Game/GameTable';
import { GameHostApi, SocketPayload } from '../Components/Game/types';
import RedesignConfirmModal from '../Components/Modals/RedesignConfirmModal';
import { RootStackParamList } from '../navigator';

const DUEL_GAME_LEGACY_ROOM_ID = 5;
const DUEL_GAME_ROOM_SLUG = 'vip';
const PREPARING_LIMIT_MS = 30000;

type NavType = NativeStackNavigationProp<RootStackParamList>;

const DuelsScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();

	const connection = useSelector(selectDuelsConnection);
	const hub = useSelector(selectDuelsHub);
	const hubErrorKey = useSelector(selectDuelsHubErrorKey);
	const current = useSelector(selectCurrentDuel);
	const joining = useSelector(selectDuelsJoining);
	const joinErrorKey = useSelector(selectDuelsJoinErrorKey);
	const playingDuelId = useSelector(selectPlayingDuelId);
	const playing = useSelector(selectDuelSnapshot(playingDuelId));
	const pendingResultDuelId = useSelector(selectPendingResultDuelId);
	const quitting = useSelector(selectDuelsQuitting);
	const quitErrorKey = useSelector(selectDuelsQuitErrorKey);
	const [preparingTooLong, setPreparingTooLong] = useState(false);
	const [hudOpen, setHudOpen] = useState(false);
	const [confirmLeave, setConfirmLeave] = useState(false);
	const [resultOverlayDuelId, setResultOverlayDuelId] = useState<string | null>(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const profileSoundOn = useSelector(
		(state: RootState) => state.auth.user?.isSoundEffectsEnabled !== false,
	);
	const [soundOn, setSoundOn] = useState(profileSoundOn);
	const soundOnRef = useRef(soundOn);
	soundOnRef.current = soundOn;
	const unreadCount = useSelector(selectUnreadCount);
	const hostApiRef = useRef<GameHostApi | null>(null);
	const menuOpenRef = useRef(menuOpen);
	menuOpenRef.current = menuOpen;
	const isFocused = useIsFocused();
	const hudLayout = useDuelHudLayout();
	const phoneLandscapeRef = useRef(hudLayout.phoneLandscape);
	phoneLandscapeRef.current = hudLayout.phoneLandscape;

	const stopWatchingMessages = useRef<(() => void) | null>(null);

	useEffect(() => {
		dispatch(duelsScreenMounted());
		dispatch(notificationsRequest());
		return () => {
			dispatch(duelsScreenUnmounted());
			if (stopWatchingMessages.current) {
				stopWatchingMessages.current();
				stopWatchingMessages.current = null;
				NativeModules.GameOrientation?.lockPortrait();
			}
		};
	}, [dispatch]);

	useFocusEffect(
		useCallback(() => {
			dispatch(duelsHubRequest());
		}, [dispatch]),
	);

	useEffect(() => {
		if (!pendingResultDuelId || !isFocused) return;
		dispatch(duelsResultOpened());
		setResultOverlayDuelId(pendingResultDuelId);
	}, [dispatch, isFocused, pendingResultDuelId]);

	const tableId = playing?.currentPlayerTableId ?? null;
	const tableShown = !!(playingDuelId && tableId);
	const overlayOpen = !!resultOverlayDuelId;
	const keepGameOrientations = tableShown || !!pendingResultDuelId || overlayOpen;

	useEffect(() => {
		if (!tableShown) setSoundOn(profileSoundOn);
	}, [profileSoundOn, tableShown]);

	useEffect(() => {
		setHudOpen(false);
		setConfirmLeave(false);
		setMenuOpen(false);
	}, [tableId]);

	useEffect(() => {
		if (quitErrorKey) setHudOpen(true);
	}, [quitErrorKey]);

	useFocusEffect(
		useCallback(() => {
			if (Platform.OS === 'web') return;
			stopWatchingMessages.current?.();
			stopWatchingMessages.current = null;
			if (!keepGameOrientations) {
				NativeModules.GameOrientation?.lockPortrait();
				return;
			}
			NativeModules.GameOrientation?.allowGameOrientations();
			return () => {
				const topRoute = () => {
					const state = navigation.getState();
					return state?.routes[state.index]?.name;
				};
				if (topRoute() !== 'Messages') {
					NativeModules.GameOrientation?.lockPortrait();
					return;
				}
				const unsubscribe = navigation.addListener('state', () => {
					const top = topRoute();
					if (top === 'Messages') return;
					unsubscribe();
					stopWatchingMessages.current = null;
					if (top !== 'Duels') NativeModules.GameOrientation?.lockPortrait();
				});
				stopWatchingMessages.current = unsubscribe;
			};
		}, [keepGameOrientations, navigation]),
	);

	const blockedAction = useRef<NavigationAction | null>(null);
	usePreventRemove(tableShown || overlayOpen, ({ data }) => {
		const isBack = ['GO_BACK', 'POP'].includes(data.action.type);
		if (isBack && tableShown && menuOpenRef.current) {
			setMenuOpen(false);
			return;
		}
		if (!isBack) blockedAction.current = data.action;
		if (tableShown) {
			dispatch(duelsPlayStop());
			dispatch(duelsHubRequest());
		} else {
			setResultOverlayDuelId(null);
		}
	});
	useEffect(() => {
		if (tableShown || overlayOpen || !blockedAction.current) return;
		const action = blockedAction.current;
		blockedAction.current = null;
		navigation.dispatch(action);
	}, [navigation, overlayOpen, tableShown]);

	const closeResultOverlay = useCallback(() => setResultOverlayDuelId(null), []);
	const resultOverlay = resultOverlayDuelId ? (
		<DuelRunCompleteOverlay
			duelId={resultOverlayDuelId}
			visible={isFocused}
			onClose={closeResultOverlay}
			onSettled={() => {
				closeResultOverlay();
				navigation.navigate('DuelResult', { duelId: resultOverlayDuelId });
			}}
			onHistory={() => navigation.navigate('DuelHistory')}
			onBreakdown={() => navigation.navigate('DuelScoreBreakdown', { duelId: resultOverlayDuelId })}
		/>
	) : null;

	useEffect(() => {
		setPreparingTooLong(false);
		if (!playingDuelId || tableId) return;
		const timer = setTimeout(() => setPreparingTooLong(true), PREPARING_LIMIT_MS);
		return () => clearTimeout(timer);
	}, [playingDuelId, tableId]);

	const handleGameEvent = useCallback(
		(event: string, data?: SocketPayload) => {
			if (event === 'goToLobby' || event === 'tableDoesNotExist') {
				dispatch(duelsPlayStop());
				dispatch(duelsHubRequest());
			}
			if (event === 'localPlayerTurnStarted') {
				setMenuOpen(false);
				if (phoneLandscapeRef.current) setHudOpen(false);
			}
			if (event === 'openNotifications') {
				setMenuOpen(false);
				navigation.navigate('Messages');
			}
			if (event === 'setSound') {
				const detail = data as { setSound?: unknown } | boolean | undefined;
				const next = typeof detail === 'boolean' ? detail : detail?.setSound;
				if (typeof next !== 'boolean' || next === soundOnRef.current) return;
				setSoundOn(next);
				if (next) playSound('ButtonPressed', { force: true });
				dispatch(updateProfileRequest({ isSoundEffectsEnabled: next }));
			}
		},
		[dispatch, navigation],
	);

	const handleBack = useCallback(() => {
		if (navigation.canGoBack()) navigation.goBack();
		else navigation.navigate('Main');
	}, [navigation]);

	if (playingDuelId && tableId && playing) {
		return (
			<View style={styles.table}>
				<GameTable
					key={tableId}
					tableId={tableId}
					legacyRoomId={DUEL_GAME_LEGACY_ROOM_ID}
					roomSlug={DUEL_GAME_ROOM_SLUG}
					isTournamentRoom={false}
					isDuel
					onGameEvent={handleGameEvent}
					hostApiRef={hostApiRef}
				/>
				<DuelHud
					duel={playing}
					open={hudOpen}
					onToggle={() => {
						setMenuOpen(false);
						setHudOpen(open => !open);
					}}
					menuOpen={menuOpen}
					onMenuToggle={() => setMenuOpen(open => !open)}
					onMenuClose={() => setMenuOpen(false)}
					soundOn={soundOn}
					unreadCount={unreadCount}
					onSound={() => hostApiRef.current?.toggleSound()}
					onGameSettings={() => {
						setMenuOpen(false);
						hostApiRef.current?.openSettings();
					}}
					onNotifications={() => {
						setMenuOpen(false);
						navigation.navigate('Messages');
					}}
					onHistory={() => navigation.navigate('DuelHistory')}
					onLeave={() => setConfirmLeave(true)}
					leaving={quitting}
					leaveErrorKey={quitErrorKey}
				/>
				{confirmLeave ? (
					<RedesignConfirmModal
						title={duelText(t, 'T_SITE_DUELS_HUD_LEAVE_TITLE')}
						message={duelText(t, 'T_SITE_DUELS_HUD_LEAVE_BODY')}
						cancelLabel={t('T_Cancel', { defaultValue: 'Cancel' })}
						confirmLabel={duelText(t, 'T_SITE_DUELS_HUD_LEAVE_CONFIRM')}
						supportedOrientations={DUEL_MODAL_ORIENTATIONS}
						onCancel={() => setConfirmLeave(false)}
						onConfirm={() => {
							setConfirmLeave(false);
							dispatch(duelsQuitRequest(playingDuelId));
						}}
					/>
				) : null}
			</View>
		);
	}

	return (
		<>
			<DuelsPage title={duelText(t, 'T_SITE_DUELS_TITLE')} onBack={handleBack}>
				{({ width, scale }) => {
					if (playingDuelId) {
						return (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(
									t,
									preparingTooLong ? 'T_SITE_DUELS_PREPARING_SLOW' : 'T_SITE_DUELS_PREPARING',
								)}
								actionLabel={duelText(
									t,
									preparingTooLong ? 'T_SITE_DUELS_RETRY' : 'T_SITE_DUELS_BACK_TO_DUELS',
								)}
								onAction={() => {
									if (preparingTooLong) {
										setPreparingTooLong(false);
										dispatch(duelsCurrentRefresh());
									} else {
										dispatch(duelsPlayStop());
									}
								}}
							/>
						);
					}

					return (
						<>
							{connection === 'unavailable' ? (
								<DuelNotice
									scale={scale}
									width={width}
									text={duelText(t, 'T_SITE_DUELS_ERROR_UNAVAILABLE')}
								/>
							) : null}
							{connection === 'replaced' ? (
								<DuelNotice
									scale={scale}
									width={width}
									text={duelText(t, 'T_SITE_DUELS_OPENED_ELSEWHERE')}
									actionLabel={duelText(t, 'T_SITE_DUELS_RECONNECT')}
									onAction={() => dispatch(duelsReconnectRequest())}
								/>
							) : null}

							{current ? (
								<DuelInProgressCard
									width={width}
									scale={scale}
									duel={current}
									onViewResult={() => navigation.navigate('DuelResult', { duelId: current.id })}
									onResume={() => dispatch(duelsPlayResume(current.id))}
								/>
							) : (
								<DuelHero
									width={width}
									scale={scale}
									finding={joining}
									disabled={connection !== 'connected'}
									onFind={() => dispatch(duelsJoinRequest())}
								/>
							)}

							{joinErrorKey ? (
								<DuelNotice
									scale={scale}
									width={width}
									text={duelText(t, joinErrorKey)}
									actionLabel={duelText(t, 'T_SITE_DUELS_OK')}
									onAction={() => dispatch(duelsJoinErrorDismiss())}
								/>
							) : null}

							{connection === 'connecting' && !current ? (
								<Text style={[duelTextStyles.muted, { fontSize: 12 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_CONNECTING')}
								</Text>
							) : null}

							{hub ? (
								<>
									<DuelDivisionCard
										width={width}
										scale={scale}
										rating={hub.rating}
										onPress={() => navigation.navigate('Ranking', { category: 'duels' })}
									/>
									<DuelSeasonCard
										width={width}
										scale={scale}
										season={hub.season}
										onPress={() => navigation.navigate('DuelSeason')}
									/>
								</>
							) : hubErrorKey ? (
								<DuelNotice
									scale={scale}
									width={width}
									text={duelText(t, hubErrorKey)}
									actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
									onAction={() => dispatch(duelsHubRequest())}
								/>
							) : (
								<DuelLoading />
							)}

							<DuelHistoryShortcut
								width={width}
								scale={scale}
								onPress={() => navigation.navigate('DuelHistory')}
							/>
						</>
					);
				}}
			</DuelsPage>
			{resultOverlay}
		</>
	);
};

const styles = StyleSheet.create({
	table: {
		flex: 1,
		backgroundColor: '#000',
	},
});

export default DuelsScreen;
